import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    ConfigurationAutomaticBillableConceptRuleCreateRequest,
    ConfigurationAutomaticBillableConceptRulePatchInBatchRequest,
    ConfigurationAutomaticBillableConceptRuleResponse
} from '@/modules/configurationModule/detail/automation/billable-concepts/components/services/configuration-automatic-billable-concept/configuration-automatic-billable-concept-rule.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import {
    ConfigurationAutomaticBillableConceptsGridState,
    initialConfigurationAutomaticBillableConceptsGridState
} from '@/modules/configurationModule/detail/automation/billable-concepts/components/automatic-billable-concept-rules-grid/configuration-automatic-billable-concept-rules-grid.model'
import ConfigurationAutomaticBillableConceptRulesModal
    from '@/modules/configurationModule/detail/automation/billable-concepts/components/billable-concept-rules-modal/configuration-automatic-billable-concept-rules-modal.vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        GridDisplayOptions,
        ConfigurationAutomaticBillableConceptRulesModal
    }
})
export default class ConfigurationAutomaticBillableConceptRulesGrid extends BaseCrudServerSideComponent<ConfigurationAutomaticBillableConceptRuleResponse> implements BaseCrud {
    @Ref() readonly automaticBillableConceptsModalWrapper!: any

    state: ConfigurationAutomaticBillableConceptsGridState
    constructor() {
        super()
        this.state = initialConfigurationAutomaticBillableConceptsGridState()
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(automaticBillableConceptRule: ConfigurationAutomaticBillableConceptRuleResponse = this.state.selectedRows[0]): void {
        if (automaticBillableConceptRule) {
            this.showModal(this.$tc('configuration.actions.update'), automaticBillableConceptRule, this.automaticBillableConceptsModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assay => assay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.automaticBillableConceptsModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    showTypeOfClientModalBasicForm(title: string) {
        this.state.actionTitle = title
        this.automaticBillableConceptsModalWrapper.show()
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, automaticBillableConceptRule: ConfigurationAutomaticBillableConceptRuleResponse[]): Promise<void> {
        if (automaticBillableConceptRule?.length) {
            const batchRequests = automaticBillableConceptRule.filter(automaticBillableConceptRule => automaticBillableConceptRule.active !== active)
                .map(automaticBillableConceptRule => ({
                    id: automaticBillableConceptRule.id,
                    active
                }))
            if (await this._patchAutomaticBillableConceptRule(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationAutomaticBillableConceptRuleCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createAutomaticBillableConceptRule(createRequest)) {
                this.automaticBillableConceptsModalWrapper.hide()
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationAutomaticBillableConceptRulePatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAutomaticBillableConceptRule([batchRequest])) {
                this.automaticBillableConceptsModalWrapper.hide()
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAutomaticBillableConceptRules(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    // SERVICE CALLS ///

    private async _createAutomaticBillableConceptRule(createRequest: ConfigurationAutomaticBillableConceptRuleCreateRequest): Promise<StrictNullable<ConfigurationAutomaticBillableConceptRuleResponse>> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchAutomaticBillableConceptRule(batchRequests: ConfigurationAutomaticBillableConceptRulePatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response && response.length !== batchRequests.length) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteAutomaticBillableConceptRules(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }
}
